/**
 * Motion helpers. The whole spec is three durations, one seal draw, and one easing:
 * 120ms press, 180ms hover/route, 280ms sheet/dialog/arc, 1050ms seal draw,
 * `cubic-bezier(.2,0,0,1)` everywhere. Animation is confirmation, never decoration:
 * one-shot only, nothing loops, nothing animates on scroll.
 *
 * `prefers-reduced-motion: reduce` zeroes every duration globally in tokens.css. These
 * helpers do the JS half: `animateOnce` simply does not run.
 */

/** The single easing curve. No other curve exists in this product. */
export const EASING = "cubic-bezier(.2,0,0,1)" as const;

export type DurationToken = "instant" | "fast" | "base" | "seal";

/** Used when there is no document (SSR, tests) or the token is missing. */
export const DURATION_FALLBACK: Record<DurationToken, number> = {
  instant: 120,
  fast: 180,
  base: 280,
  seal: 1050,
};

const CSS_VAR: Record<DurationToken, string> = {
  instant: "--duration-instant",
  fast: "--duration-fast",
  base: "--duration-base",
  seal: "--duration-seal",
};

/** "280ms" | "0.28s" | "" -> milliseconds. Returns null when it cannot be read. */
export function parseCssDuration(raw: string): number | null {
  const value = raw.trim();
  if (!value) return null;
  const match = /^(-?[\d.]+)(ms|s)$/.exec(value);
  if (!match) return null;
  const n = Number(match[1]);
  if (!Number.isFinite(n) || n < 0) return null;
  return match[2] === "s" ? n * 1000 : n;
}

/** Reads the four duration tokens off :root, falling back to the spec values. */
export function readDurations(): Record<DurationToken, number> {
  const out = { ...DURATION_FALLBACK };
  if (typeof document === "undefined" || !document.documentElement) return out;
  const style = getComputedStyle(document.documentElement);
  for (const token of Object.keys(CSS_VAR) as DurationToken[]) {
    const ms = parseCssDuration(style.getPropertyValue(CSS_VAR[token]));
    // A reduced-motion root reports 0.001ms. That is a real value and we keep it.
    if (ms !== null) out[token] = ms;
  }
  return out;
}

/**
 * The durations, read once at module load. Components import these instead of typing a
 * number, so a token change moves every animation at once.
 *
 * ponytail: read once, not per call. A duration token never changes at runtime; theme
 * switching does not touch it. If that ever becomes false, call `readDurations()`.
 */
export const DURATIONS: Record<DurationToken, number> = readDurations();

/** True when the viewer asked the OS for less motion. False when we cannot tell. */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

export type AnimateOnceOptions = {
  /** One of the four spec durations, in ms. */
  duration: number;
  easing?: string;
  delay?: number;
  fill?: FillMode;
};

/**
 * Runs a keyframe animation exactly once. Returns the `Animation` so a caller can await
 * `finished`, or `null` when nothing ran (reduced motion, no element, no WAAPI).
 *
 * `iterations` is hard-wired to 1 and is not an option: nothing in HourSign loops.
 */
export function animateOnce(
  el: Element | null | undefined,
  keyframes: Keyframe[],
  { duration, easing = EASING, delay = 0, fill = "none" }: AnimateOnceOptions,
): Animation | null {
  if (!el) return null;
  if (prefersReducedMotion()) return null;
  if (typeof (el as HTMLElement).animate !== "function") return null;
  return (el as HTMLElement).animate(keyframes, {
    duration,
    easing,
    delay,
    fill,
    iterations: 1,
  });
}

/** The route-transition keyframes: 180ms fade + 8px rise. Content region only. */
export const RISE_IN: Keyframe[] = [
  { opacity: 0, transform: "translateY(8px)" },
  { opacity: 1, transform: "translateY(0)" },
];
